import { useMemo } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CONTINUOUS_DYNAMICS } from "@/components/method/continuousDynamics";

/**
 * The Yadid Embrace Method — two symmetric arcs framed by four editable
 * surrounding labels.
 *
 * Semantic-token migration (Feb 2026):
 *   • Movement names (Listen / Reveal / …) → foreground (primary text).
 *   • Framework labels around the diagram → muted-foreground.
 *   • Arc strokes → muted-foreground at low opacity (restrained neutral).
 *   • Baseline / grain / mindset labels → muted-foreground.
 *   • BRASS is reserved for meaningful states ONLY:
 *       – the phase that is the CURRENT primary emphasis (node fill + caption).
 *       – the SELECTED phase underline while the drawer is open.
 *     No brass on the arcs themselves, no brass on default framework labels.
 */

const NODE_X = { listen: 280, reveal: 600, reimagine: 800, guide: 1120 };
const BASELINE_Y = 280;
const ARC_AMPLITUDE = 100;
const NODE_R = 9;
const PIVOT_GAP = 6;

const lensPath = (x1, x2, direction) => {
  const y1 = BASELINE_Y + (direction === "top" ? -ARC_AMPLITUDE : ARC_AMPLITUDE);
  const c1x = x1 + (x2 - x1) * 0.25;
  const c2x = x1 + (x2 - x1) * 0.75;
  return `M ${x1} ${BASELINE_Y} C ${c1x} ${y1}, ${c2x} ${y1}, ${x2} ${BASELINE_Y}`;
};
const LENS_1_TOP = lensPath(NODE_X.listen, NODE_X.reveal, "top");
const LENS_1_BOT = lensPath(NODE_X.listen, NODE_X.reveal, "bot");
const LENS_2_TOP = lensPath(NODE_X.reimagine, NODE_X.guide, "top");
const LENS_2_BOT = lensPath(NODE_X.reimagine, NODE_X.guide, "bot");
const PIVOT_X1 = NODE_X.reveal + NODE_R + PIVOT_GAP;
const PIVOT_X2 = NODE_X.reimagine - NODE_R - PIVOT_GAP;

const MINDSET_Y_TOP = 140;
const MINDSET_ANCHORS = {
  listen: { x: (NODE_X.listen + NODE_X.reveal) / 2 - 90, text: "Diverge · Sit with it" },
  reveal: { x: (NODE_X.listen + NODE_X.reveal) / 2 + 90, text: "Converge · Name it" },
  reimagine: { x: (NODE_X.reimagine + NODE_X.guide) / 2 - 90, text: "Diverge · Design many" },
  guide: { x: (NODE_X.reimagine + NODE_X.guide) / 2 + 90, text: "Converge · Walk with it" },
};

const PHASE_KEYS = ["listen", "reveal", "reimagine", "guide"];

// Semantic-token colors expressed as CSS var references. Works in SVG
// presentation attributes via inline `style` in modern browsers.
const CSS = {
  foreground: "hsl(var(--foreground))",
  muted:     "hsl(var(--muted-foreground))",
  border:    "hsl(var(--border))",
  brass:     "hsl(var(--brass))",
  brassSoft: "hsl(var(--brass) / 0.28)",
  neutralStroke: "hsl(var(--muted-foreground) / 0.55)",
  neutralSoft:   "hsl(var(--muted-foreground) / 0.25)",
};

export default function MethodArcs({
  phases = [],
  currentPhase = null,
  onSelect,
  selected = null,
  activeDynamic = null,
  onSelectDynamic = null,
  frameworkLabels = {
    top: "Connections and Relationships",
    bottom: "Leadership and Storytelling",
    left: "Orientation and Vision Setting",
    right: "Continuing the Journey",
  },
}) {
  const byKey = useMemo(() => Object.fromEntries((phases || []).map((p) => [p.key, p])), [phases]);
  // Continuous dynamics live on the top + bottom framework labels — they are
  // NOT phases, so we render them as buttons rather than phase nodes. Hover
  // preview surfaces the framing question via shadcn tooltip; click pins the
  // full explainer card below the arcs (owned by the parent page).
  const renderDynamicLabel = (position, dynamicKey) => {
    const dynamic = CONTINUOUS_DYNAMICS[dynamicKey];
    const label = frameworkLabels[position] || dynamic?.label || "";
    const isActive = activeDynamic === dynamicKey;
    const interactive = !!onSelectDynamic;
    const baseStyle = {
      fontFamily: "ui-monospace, Menlo, monospace",
      fontSize: "0.6562rem",
      letterSpacing: "0.28em",
      textTransform: "uppercase",
      textAlign: "center",
      lineHeight: 1.4,
      color: isActive ? CSS.brass : CSS.muted,
      background: "transparent",
      border: "none",
      padding: 0,
      cursor: interactive ? "pointer" : "default",
      transition: "color 160ms ease",
    };
    const content = (
      <button
        type="button"
        onClick={interactive ? () => onSelectDynamic(dynamicKey) : undefined}
        data-testid={`dynamic-label-${dynamicKey}`}
        aria-pressed={isActive}
        aria-label={dynamic ? `${dynamic.shortLabel} · continuous dynamic. ${isActive ? "Pinned. Activate to close." : "Activate to pin explainer below the arcs."}` : label}
        style={baseStyle}
        className={interactive ? "hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-brass/50" : undefined}
      >
        <span
          style={{
            borderBottom: isActive ? `1px solid ${CSS.brass}` : "1px solid transparent",
            paddingBottom: 2,
          }}
        >
          {label}
        </span>
      </button>
    );
    if (!interactive || !dynamic) return content;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent
            side={position === "top" ? "bottom" : "top"}
            className="max-w-sm text-xs leading-relaxed"
            data-testid={`dynamic-tooltip-${dynamicKey}`}
          >
            {dynamic.framingQuestion}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="w-full" data-testid="method-arcs">
      <svg
        viewBox="0 0 1400 640"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Yadid Embrace Method — Listen, Reveal, Reimagine, Guide"
        style={{ width: "100%", height: "auto", display: "block", maxHeight: 620 }}
      >
        {/* Framework label · TOP — Continuous dynamic (Connections & Relationships).
            Interactive when a click handler is provided; static text otherwise. */}
        <foreignObject x="360" y="20" width="680" height="46">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ textAlign: "center" }} data-testid="framework-label-top">
            {renderDynamicLabel("top", "connections")}
          </div>
        </foreignObject>

        {/* Framework label · LEFT */}
        <foreignObject x="18" y="230" width="180" height="120">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{
            fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.6562rem",
            color: CSS.muted, letterSpacing: "0.24em", textTransform: "uppercase",
            textAlign: "right", lineHeight: 1.55, paddingRight: 12,
          }} data-testid="framework-label-left">
            {frameworkLabels.left}
          </div>
        </foreignObject>

        {/* Framework label · RIGHT */}
        <foreignObject x="1202" y="230" width="180" height="120">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{
            fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.6562rem",
            color: CSS.muted, letterSpacing: "0.24em", textTransform: "uppercase",
            textAlign: "left", lineHeight: 1.55, paddingLeft: 12,
          }} data-testid="framework-label-right">
            {frameworkLabels.right}
          </div>
        </foreignObject>

        {/* Baseline spine — soft neutral so it recedes */}
        <line x1="200" y1={BASELINE_Y} x2="1200" y2={BASELINE_Y}
              style={{ stroke: CSS.neutralSoft }} strokeWidth="1" />

        {/* Mindset labels above arcs */}
        {PHASE_KEYS.map((k) => {
          const a = MINDSET_ANCHORS[k];
          return (
            <text key={"mind-" + k} x={a.x} y={MINDSET_Y_TOP} textAnchor="middle"
                  style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "0.8125rem", fill: CSS.muted }}>
              {a.text}
            </text>
          );
        })}

        {/* Lens 1 (Listen → Reveal) — restrained neutral */}
        <path d={LENS_1_TOP} fill="none" style={{ stroke: CSS.neutralStroke }} strokeWidth="1.4" />
        <path d={LENS_1_BOT} fill="none" style={{ stroke: CSS.neutralStroke }} strokeWidth="1.4" />

        {/* Pivot connector */}
        <path d={`M ${PIVOT_X1} ${BASELINE_Y} L ${PIVOT_X2} ${BASELINE_Y}`}
              style={{ stroke: CSS.neutralSoft }} strokeWidth="1" strokeDasharray="3 5" />
        <text x={(PIVOT_X1 + PIVOT_X2) / 2} y={BASELINE_Y - 12} textAnchor="middle"
              style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "0.6875rem", fill: CSS.muted }}>
          pivot
        </text>

        {/* Lens 2 (Reimagine → Guide) */}
        <path d={LENS_2_TOP} fill="none" style={{ stroke: CSS.neutralStroke }} strokeWidth="1.4" />
        <path d={LENS_2_BOT} fill="none" style={{ stroke: CSS.neutralStroke }} strokeWidth="1.4" />

        {/* Phase nodes */}
        {PHASE_KEYS.map((k) => {
          const p = byKey[k];
          const x = NODE_X[k];
          const isCurrent = currentPhase === k;
          const isSelected = selected === k;
          // Brass ONLY marks the current primary emphasis. Every other node
          // is a neutral outlined dot on the muted stroke color.
          const nodeFill   = isCurrent ? CSS.brass : "hsl(var(--card))";
          const nodeStroke = isCurrent ? CSS.brass : CSS.muted;
          return (
            <g key={k} onClick={() => onSelect?.(k)} style={{ cursor: onSelect ? "pointer" : "default" }} data-testid={`method-node-${k}`}>
              <circle cx={x} cy={BASELINE_Y} r={NODE_R}
                      style={{ fill: nodeFill, stroke: nodeStroke }} strokeWidth="1.5" />
              {/* Client-facing movement name — primary text color */}
              <text x={x} y={BASELINE_Y + 60} textAnchor="middle"
                    style={{
                      fontFamily: "Georgia, serif", fontSize: "1.375rem", fontWeight: 700,
                      fill: CSS.foreground, letterSpacing: "0.005em",
                    }}>
                {p?.client_label || k}
              </text>
              {/* Framework descriptor — muted */}
              <text x={x} y={BASELINE_Y + 82} textAnchor="middle"
                    style={{
                      fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.625rem",
                      fill: CSS.muted, letterSpacing: "0.26em", textTransform: "uppercase",
                    }}>
                {p?.framework_label || ""}
              </text>
              {p?.intent && (() => {
                const isInside = k === "reveal" || k === "reimagine";
                const w = isInside ? 176 : 260;
                return (
                  <foreignObject x={x - w / 2} y={BASELINE_Y + 96} width={w} height="110">
                    <div xmlns="http://www.w3.org/1999/xhtml" style={{
                      fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "0.75rem",
                      color: CSS.muted, lineHeight: 1.5, textAlign: "center",
                    }}>
                      {p.intent}
                    </div>
                  </foreignObject>
                );
              })()}
              {/* Current-emphasis caption — Brass */}
              {isCurrent && (
                <text x={x} y={BASELINE_Y + 200} textAnchor="middle"
                      style={{
                        fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.5625rem",
                        fill: CSS.brass, letterSpacing: "0.24em", textTransform: "uppercase",
                      }}>
                  · current emphasis ·
                </text>
              )}
              {/* Selection underline — Brass, only while drawer is open */}
              {isSelected && (
                <line x1={x - 18} y1={BASELINE_Y + 90} x2={x + 18} y2={BASELINE_Y + 90}
                      style={{ stroke: CSS.brass }} strokeWidth="1.5" opacity="0.85" />
              )}
            </g>
          );
        })}

        {/* Framework label · BOTTOM — Continuous dynamic (Leadership & Storytelling). */}
        <foreignObject x="360" y="588" width="680" height="46">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ textAlign: "center" }} data-testid="framework-label-bottom">
            {renderDynamicLabel("bottom", "leadership")}
          </div>
        </foreignObject>
      </svg>
    </div>
  );
}
